import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'primereact/button'
import { useSelector } from 'react-redux'
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { Message } from 'primereact/message'

import { LandingPageLayout } from '../../../lib/landing-page-layout'
import { theme } from '../../../lib/theme'
import {
    useCompanyGetCompanyByIdQuery,
    useCompanyTransferGetPartnerQuery,
    useCompanyTransferRequestTransferMutation,
} from '../../../common/redux/api/web-api'
import { RootState } from '../../redux/store'
import { HeaderChangeOrganisationDropdown } from '../../components/HeaderChangeOrganisationDropdown'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import { LandingMessage, LandingMessageType } from '../../../lib/landing-message'
import { useToast } from '../../../common/uikit/ToastProvider'
import { isFetchBaseQueryError } from '../../../common/redux/api/fetchBaseQueryWithReauth'

export const ChangeOperatorPage = () => {
    const { transferKey } = useParams()
    const navigate = useNavigate()
    const text = useText()
    const toast = useToast()

    const [confirmTransfer, setConfirmTransfer] = useState(false)

    const handleConfirmChange = (e: CheckboxChangeEvent) => {
        setConfirmTransfer(Boolean(e.checked))
    }

    const { selectedCompanyId } = useSelector((state: RootState) => state.user)
    const { data: companyInfo } = useCompanyGetCompanyByIdQuery(
        { companyId: selectedCompanyId },
        { skip: !selectedCompanyId }
    )

    const { data: partner, error: partnerError } = useCompanyTransferGetPartnerQuery(
        { transferKey: transferKey ?? '', companyId: selectedCompanyId },
        { skip: !transferKey || !selectedCompanyId }
    )
    const [requestTransfer] = useCompanyTransferRequestTransferMutation()

    const renderAlreadyBelongsToPartnerContent = () => {
        return (
            <LandingMessage
                type={LandingMessageType.SUCCESS}
                title={text('lblTransferToANewPartner')}
                linkText={text('btnGoToDashboard')}
                onClick={() => {
                    navigate('/dashboard', { replace: true })
                }}
            >
                <p>
                    <Text
                        code="msgTransferOrganisationAlreadyBelongsToPartner"
                        values={{ partnerName: partner?.name }}
                    />
                </p>
            </LandingMessage>
        )
    }

    const renderInvalidOrExpiredContent = () => {
        return (
            <LandingMessage
                title={text('lblTransferLinkIsInvalid')}
                linkText={text('btnGoToDashboard')}
                onClick={() => {
                    navigate('/dashboard', { replace: true })
                }}
                type={LandingMessageType.ERROR}
            >
                <div data-testid="message">{text('msgTransferInvalidLink')}</div>
            </LandingMessage>
        )
    }

    const renderDefaultContent = () => {
        return (
            <LandingMessage title={text('lblTransferToANewPartner')}>
                <p>
                    <Text
                        code="msgDoYouWantToChangePartner"
                        values={{
                            fromPartner: companyInfo?.partnerName,
                            toPartner: partner?.name,
                            organisation: companyInfo?.name,
                        }}
                    />
                </p>

                <p>{text('msgAfterTheTransferInfo')}</p>

                <Message
                    severity="error"
                    className="w-full mb-3"
                    content={
                        <p className="m-0 p-0 text-left">
                            <Text
                                code="msgTransferConfirmationWarning"
                                values={{
                                    currentPartner: companyInfo?.partnerName,
                                    organisationName: companyInfo?.name,
                                }}
                            />
                        </p>
                    }
                />

                <div className="flex align-items-center mb-4">
                    <Checkbox
                        inputId="confirmTransfer"
                        checked={confirmTransfer}
                        onChange={handleConfirmChange}
                        className="mr-2"
                    />
                    <label htmlFor="confirmTransfer" className="cursor-pointer mb-0">
                        {text('msgTransferConfirmation')}
                    </label>
                </div>

                <div className="flex justify-content-between gap-2 mt-4">
                    <Button
                        outlined
                        label={text('btnCancel')}
                        data-testid="cancel-btn"
                        onClick={() => navigate('/dashboard', { replace: true })}
                    />
                    <Button
                        label={text('btnApprove')}
                        data-testid="approve-btn"
                        disabled={!confirmTransfer}
                        onClick={() => {
                            if (transferKey && selectedCompanyId) {
                                requestTransfer({ transferKey, companyId: selectedCompanyId })
                                    .unwrap()
                                    .then(() => {
                                        navigate('/change-operator-success', { replace: true })
                                    })
                                    .catch((error) => {
                                        if (isFetchBaseQueryError(error)) {
                                            if (error.status === 403) {
                                                toast.notifyError(text('msgTransferInsufficientPermissions'))
                                            } else {
                                                toast.notifyError(text('lblSomethingWentWrong'))
                                            }
                                        }
                                    })
                            }
                        }}
                    />
                </div>
            </LandingMessage>
        )
    }

    const renderContent = () => {
        if (partnerError) {
            return renderInvalidOrExpiredContent()
        }
        if (partner && companyInfo) {
            if (partner?.id === companyInfo?.partnerId) {
                return renderAlreadyBelongsToPartnerContent()
            }
            return renderDefaultContent()
        }
        return null
    }

    return (
        <LandingPageLayout
            background={theme.whiteBackground}
            hideLanguageSelect
            headerContent={<HeaderChangeOrganisationDropdown />}
        >
            {renderContent()}
        </LandingPageLayout>
    )
}
